"use strict";

import Geometry from "ol/geom/Geometry";
import Vector from "ol/source/Vector";
import VectorLayer from "ol/layer/Vector";
import Feature from "ol/Feature";

/**
 * @classdesc
 * Openlayers-Implementierung für einen Tracking-Layer.
 *
 * @class
 * @extends gc.gis.layer.GCTrackingLayer
 * @memberof gc.gis.layer
 * @author Christian Schmidt <c.schmidt@brain-scc.de>
 *
 * @param {gc.gis.map.GCAbstractMap} map Karte, welche die Objekte des Tracking-Layers darstellen soll
 * @param {Object} [opt_options] optionale Optionen
 * @param {boolean} [opt_options.allowClearDrawingArea=false] Flag welches anzeigt, ob die Inhalte des Tracking-Layer
 *      durch die Funktion "Zeichenfläche leeren", verändert werden dürfen
 * @param {int} [opt_options.drawOrder] Zeichenreihenfolge des Trackinglayer in der Liste aller Trackinglayer
 * @param {gc.gis.action.GCAbstractAction} [opt_options.clearDrawingAreaAction] Spezielle Aktion die ausgeführt wird,
 *      wenn die Funktion "Zeichenfläche leeren", für den Layer ausgelöst wird (Voraussetzung: das Flag
 *      opt_options.allowClearDrawingArea wurde gesetzt
 * @param {Object} [opt_options.mapLibOptions] optionale Kartenbibliothek spezifische Optionen
 */
gc.gis.layer.OLTrackingLayer = function(map, opt_options)
{
	opt_options = opt_options || {};

    gc.gis.layer.GCTrackingLayer.call(this, map, opt_options);

	const ol_options = opt_options.mapLibOptions || {};
	if (opt_options.drawOrder !== undefined)
	{
		ol_options.zIndex = opt_options.drawOrder;
	}

    ol_options.source = new Vector(); // die Source nicht als Member halten, da sie sich ggf. dynamisch zur Laufzeit ändern kann
    this._layer = new VectorLayer(ol_options);

	/**
	 * @type {gc.gis.geom.OLGeometryFactory}
	 * @private
	 */
	this._geometryFactory = new gc.gis.geom.OLGeometryFactory();

    this._initEvents();
};
gc.gis.layer.OLTrackingLayer.prototype = Object.create(gc.gis.layer.GCTrackingLayer.prototype);

/**
 * Initialisiert die Events.
 *
 * @private
 */
gc.gis.layer.OLTrackingLayer.prototype._initEvents = function()
{
    const self = this;
	this._layer.getSource().on('addfeature', function (evt) {
        self._fireEvent(gc.gis.layer.GCVectorLayerEvent.FEATURE_ADDED, {
	        feature: new gc.gis.feature.OLFeature(evt.features)
        });
    });

	this._layer.getSource().on('changefeature', function (evt) {
		self._fireEvent(gc.gis.layer.GCVectorLayerEvent.FEATURE_CHANGED, {
			feature: new gc.gis.feature.OLFeature(evt.features)
		});
	});

	this._layer.getSource().on('removefeature', function (evt) {
		self._fireEvent(gc.gis.layer.GCVectorLayerEvent.FEATURE_REMOVED, {
			feature: new gc.gis.feature.OLFeature(evt.features)
		});
	});

	this._layer.getSource().on('clear', function (evt) {
		self._fireEvent(gc.gis.layer.GCVectorLayerEvent.LAYER_CLEARED, {});
	});
};

/**
 * @inheritdoc
 */
gc.gis.layer.OLTrackingLayer.prototype.getImpl = function()
{
    return this._layer;
};

/**
 * @inheritDoc
 */
gc.gis.layer.OLTrackingLayer.prototype.setVisible = function(visible)
{
    this._layer.setVisible(visible);
};

/**
 * @inheritDoc
 */
gc.gis.layer.OLTrackingLayer.prototype.isVisible = function()
{
    return this._layer.getVisible();
};

/**
 * @inheritDoc
 */
gc.gis.layer.OLTrackingLayer.prototype.setStyle = function(styles)
{
   this._layer.setStyle(gc.gis.OLUtil.toOLStyle(styles));
};

/**
 * @inheritDoc
 */
gc.gis.layer.OLTrackingLayer.prototype.clear = function()
{
	this._layer.getSource().clear();
};

/**
 * @inheritDoc
 */
gc.gis.layer.OLTrackingLayer.prototype.getFeatures = function ()
{
    const features = [];
    let ol_features = this._layer.getSource().getFeatures(),
        feature,
	    geom,
        i, ii;
    
    for (i = 0, ii = ol_features.length; i < ii; i++)
    {
	    feature = new gc.gis.feature.OLFeature(ol_features[i]);
	    if (ol_features[i].getGeometry())
	    {
		    geom = this._geometryFactory.createGeometry(ol_features[i].getGeometry());
		    geom.setCRS(this._map.getProjection());         // FIXME: Änderung des CRS der Map wird nicht berücksichtigt (OL macht zz. keine on-the-fly-Transformation für Vectorlayer)
	    }

        features.push(feature);
    }

    return features;
};

/**
 * @inheritDoc
 */
gc.gis.layer.OLTrackingLayer.prototype.getFeatureByID = function (id)
{
	const ol_feature = this._layer.getSource().getFeatureById(id);

	return ol_feature ? new gc.gis.feature.OLFeature(ol_feature) : null;
};

/**
 * @inheritDoc
 */
gc.gis.layer.OLTrackingLayer.prototype.addGeometry = function (geometry)
{
    gc.gis.assert.isInstance(geometry, gc.gis.geom.GCGeometry);
	gc.gis.assert.isInstance(geometry.getImpl(), Geometry);

    geometry.project(this._map.getProjection());

    const ol_feature = new Feature({
        geometry: geometry.getImpl()
    });
	this._layer.getSource().addFeature(ol_feature);

    return new gc.gis.feature.OLFeature(ol_feature);
};

/**
 * @inheritDoc
 */
gc.gis.layer.GCTrackingLayer.prototype.addGeometries = function (geometries)
{
    const features = [];
    let i;

    for (i = 0; i < geometries.length; i++)
    {
        features.push(this.addGeometry(geometries[i]));
    }

    return features;
};

/**
 * @inheritDoc
 */
gc.gis.layer.OLTrackingLayer.prototype.addFeature = function(feature)
{
    gc.gis.assert.isInstance(feature, gc.gis.feature.OLFeature);

    const ol_feature = feature.getImpl();
    const geom = feature.getGeometry();

    if(geom)
    {
        geom.project(this._map.getProjection());
    }

	this._layer.getSource().addFeature(ol_feature);
};

/**
 * @inheritDoc
 */
gc.gis.layer.OLTrackingLayer.prototype.addFeatures = function(features)
{
    for (let i=0; i<features.length; i++)
    {
        this.addFeature(features[i]);
    }
};

/**
 * @inheritDoc
 */
gc.gis.layer.OLTrackingLayer.prototype.removeFeature = function(feature)
{
    const ol_feature = feature.getImpl();
    gc.gis.assert.isInstance(ol_feature, Feature);

    this._layer.getSource().removeFeature(ol_feature);
};

/**
 * @inheritDoc
 */
gc.gis.layer.OLTrackingLayer.prototype.removeFeatures = function(features)
{
    for (let i = 0, ii = features.length; i < ii; i++)
    {
        this.removeFeature(features[i]);
    }
};

/**
 * @inheritDoc
 */
gc.gis.layer.OLTrackingLayer.prototype.removeFeatureByID = function (id)
{
	const ol_feature = this._layer.getSource().getFeatureById(id);

	if (ol_feature)
	{
		this._layer.getSource().removeFeature(ol_feature);
		return true;
	}

	return false;
};

/**
 * @inheritDoc
 */
gc.gis.layer.OLTrackingLayer.prototype.numFeatures = function ()
{
    return this._layer.getSource().getFeatures().length;
};